/**
 * CombatLog — OWNER: log builder (packages/sim/src/log/**).
 * Event buffer + filters, Details!-style meters, pull segments and the WCL export.
 * Holds only derived data (events and metadata seen through ingest), never game state.
 */
import type { CombatLog, Meters, Ms, PullSegment, SimEvent, UnitId, World } from '../contracts';
import { computeMeters } from './meters';
import { membersOfPacks, metaOf, type SegmentRecord, type UnitMeta } from './meta';
import { buildWclExport } from './wcl';

export * from './meters';
export { WCL_HIT_TYPE, WCL_SCHOOL, MELEE_GAME_ID, NPC_GAME_ID_BASE } from './wcl';
export type { UnitMeta, SegmentRecord } from './meta';

export interface PullMeters {
  pull: PullSegment;
  meters: Meters;
}

/** Per-pull meters (each pull's own window). Works with any CombatLog. */
export function pullBreakdown(log: CombatLog): PullMeters[] {
  const lastTs = log.events().at(-1)?.timestamp ?? 0;
  return log.pulls().map((pull) => ({ pull, meters: log.meters({ fromMs: pull.startMs, toMs: pull.endMs ?? lastTs }) }));
}

export function createCombatLog(): CombatLog {
  let all: SimEvent[] = [];
  let meta = new Map<UnitId, UnitMeta>();
  let segments: SegmentRecord[] = [];

  const remember = (world: World, id: UnitId | undefined): void => {
    if (!id || meta.has(id)) return;
    const u = world.units[id];
    if (u) meta.set(id, metaOf(u));
  };

  const isBossPull = (world: World, memberIds: readonly UnitId[]) =>
    memberIds.some((id) => {
      const u = world.units[id];
      return !!u && (u.tier === 'boss' || u.encounterId !== null);
    });

  const toPullSegment = (s: SegmentRecord): PullSegment => ({
    pull: s.pull,
    startMs: s.startMs,
    endMs: s.endMs,
    packIds: [...s.packIds],
    mobs: s.memberIds.length,
    isBoss: s.isBoss,
    wipe: s.wipe,
  });

  return {
    ingest(events, world) {
      for (const e of events) {
        all.push(e);
        remember(world, e.sourceID);
        remember(world, e.targetID);
        if (e.type === 'absorbed') remember(world, e.attackerID);
        if (e.type === 'pull_start') {
          const memberIds = membersOfPacks(world, e.packIds);
          segments.push({ pull: e.pull, startMs: e.timestamp, endMs: null, packIds: [...e.packIds], memberIds, killedIds: [], encounterId: null, isBoss: isBossPull(world, memberIds), wipe: false });
        } else if (e.type === 'pull_end') {
          const seg = segments.find((s) => s.pull === e.pull);
          if (!seg) continue;
          seg.endMs = e.timestamp;
          seg.packIds = [...e.packIds];
          seg.killedIds = [...e.killedIds];
          seg.wipe = e.wipe;
          const members = membersOfPacks(world, e.packIds);
          for (const id of e.killedIds) if (!members.includes(id)) members.push(id);
          seg.memberIds = members;
          seg.isBoss ||= isBossPull(world, members);
          for (const id of members) remember(world, id);
        } else if (e.type === 'encounter_start') {
          const seg = segments.at(-1);
          if (seg && seg.endMs === null) {
            seg.encounterId = e.encounterId;
            seg.isBoss = true;
          }
        }
      }
    },

    events(filter) {
      if (!filter) return [...all];
      return all.filter(
        (e) =>
          (!filter.types || filter.types.includes(e.type)) &&
          (!filter.sourceId || e.sourceID === filter.sourceId) &&
          (!filter.targetId || e.targetID === filter.targetId) &&
          (filter.fromMs === undefined || e.timestamp >= filter.fromMs) &&
          (filter.toMs === undefined || e.timestamp <= filter.toMs),
      );
    },

    meters(window?: { fromMs?: Ms; toMs?: Ms }): Meters {
      return computeMeters(all, meta, segments.map(toPullSegment), window);
    },

    pulls: () => segments.map(toPullSegment),

    exportWcl(world, content) {
      return buildWclExport({ world, content, events: all, meta, segments });
    },

    clear() {
      all = [];
      meta = new Map();
      segments = [];
    },
  };
}
